import { useMutation } from '@vue/apollo-composable';
import * as documents from '@/graphql/customFields';
import type {
  CreateCustomFieldMutation, CreateCustomFieldMutationVariables,
  UpdateCustomFieldMutation, UpdateCustomFieldMutationVariables,
  DeleteCustomFieldMutation, DeleteCustomFieldMutationVariables,
  ReorderCustomFieldsMutation, ReorderCustomFieldsMutationVariables,
  ApplyCustomFieldTemplateMutation, ApplyCustomFieldTemplateMutationVariables,
  UpdateStudentCustomFieldValuesMutation, UpdateStudentCustomFieldValuesMutationVariables,
  UpdateStaffCustomFieldValuesMutation, UpdateStaffCustomFieldValuesMutationVariables,
  UpdateGuardianCustomFieldValuesMutation, UpdateGuardianCustomFieldValuesMutationVariables,
} from '@/types/operations';
import { CustomFieldEntity } from '@/types/composition-functions';

/** Mutations for school-defined custom fields (#574). */
export function useCustomFieldMutations() {
  const saveStudentValues = useMutation<UpdateStudentCustomFieldValuesMutation, UpdateStudentCustomFieldValuesMutationVariables>(
    documents.UPDATE_STUDENT_CUSTOM_FIELD_VALUES,
  );
  const saveStaffValues = useMutation<UpdateStaffCustomFieldValuesMutation, UpdateStaffCustomFieldValuesMutationVariables>(
    documents.UPDATE_STAFF_CUSTOM_FIELD_VALUES,
  );
  const saveGuardianValues = useMutation<UpdateGuardianCustomFieldValuesMutation, UpdateGuardianCustomFieldValuesMutationVariables>(
    documents.UPDATE_GUARDIAN_CUSTOM_FIELD_VALUES,
  );

  /** Save one record's changed values, whichever entity it is. */
  const saveEntityValues = (entity: CustomFieldEntity, id: string, values: Array<{ field_id: string; value: string | null }>) => {
    if (entity === CustomFieldEntity.Staff) return saveStaffValues.mutate({ staff_id: id, values });
    if (entity === CustomFieldEntity.Guardian) return saveGuardianValues.mutate({ guardian_id: id, values });
    return saveStudentValues.mutate({ student_id: id, values });
  };

  return {
    create: useMutation<CreateCustomFieldMutation, CreateCustomFieldMutationVariables>(documents.CREATE_CUSTOM_FIELD),
    update: useMutation<UpdateCustomFieldMutation, UpdateCustomFieldMutationVariables>(documents.UPDATE_CUSTOM_FIELD),
    remove: useMutation<DeleteCustomFieldMutation, DeleteCustomFieldMutationVariables>(documents.DELETE_CUSTOM_FIELD),
    reorder: useMutation<ReorderCustomFieldsMutation, ReorderCustomFieldsMutationVariables>(documents.REORDER_CUSTOM_FIELDS),
    /** Adds a template's missing fields (#574); returns the fields created or reactivated. */
    applyTemplate: useMutation<ApplyCustomFieldTemplateMutation, ApplyCustomFieldTemplateMutationVariables>(
      documents.APPLY_CUSTOM_FIELD_TEMPLATE,
    ),
    saveStudentValues,
    saveStaffValues,
    saveGuardianValues,
    saveEntityValues,
  };
}
